import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import {
  CommunicationService,
  CommunicationRule,
  CommunicationTemplate,
} from '@services/communication.service';
import { NotificationService } from '@services/notification.service';
import { AuthService } from '@services/auth.service';
import { OkCancelComponent } from '@components/admin/dialog/ok-cancel/ok-cancel.component';

@Component({
  selector: 'app-communications-center',
  templateUrl: './communications-center.component.html',
  styleUrls: ['./communications-center.component.css'],
})
export class CommunicationsCenterComponent implements OnInit {
  templates: CommunicationTemplate[] = [];
  rules: CommunicationRule[] = [];
  sends: any[] = [];
  exceptions: any[] = [];
  audit: any[] = [];
  status: any = { alerts: [] };
  selectedTemplate: CommunicationTemplate = this.blankTemplate();
  selectedRule: CommunicationRule = null;
  estimate = 0;
  testEmail = '';
  previewMode: 'desktop' | 'mobile' = 'desktop';
  previewHtml: SafeHtml = '';
  sendFilter = '';
  saving = false;
  running = false;
  testing = false;
  private preferredIds = ['rx-expiration', 'ready-pickup', 'birthday'];

  categories = ['SERVICE', 'MARKETING'];
  channels = ['EMAIL', 'TEXT', 'BOTH'];
  templateStatuses = ['DRAFT', 'ACTIVE', 'PAUSED'];
  sendStatuses = [
    '',
    'SCHEDULED',
    'SENDING',
    'SENT',
    'DELIVERED',
    'BOUNCED',
    'FAILED',
    'COMPLAINED',
    'UNSUBSCRIBED',
    'MISSING_CONTACT',
    'CANCELED',
  ];

  constructor(
    private communication: CommunicationService,
    private notification: NotificationService,
    private auth: AuthService,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer
  ) {
    this.testEmail = this.auth.currentUser?.email || '';
  }

  ngOnInit(): void {
    this.reload();
  }

  rankRows<T extends { _id?: string; name?: string }>(rows: T[]): T[] {
    return [...rows].sort((a, b) => {
      const ia = this.preferredIds.indexOf(String(a._id || ''));
      const ib = this.preferredIds.indexOf(String(b._id || ''));
      if (ia !== ib) {
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      }
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }

  blankTemplate(): CommunicationTemplate {
    return {
      _id: `custom-${Date.now()}`,
      name: 'New Template',
      category: 'SERVICE',
      channel: 'EMAIL',
      messageType: 'CUSTOM',
      subject: '',
      previewText: '',
      body: '',
      fromName: 'Rims & Goggles Mill Valley',
      fromAddress: 'noreply@autooptiq.com',
      replyTo: '',
      headerColor: '#4a7c7d',
      footer: '',
      status: 'DRAFT',
    };
  }

  reload(): void {
    this.communication.status().subscribe((status) => {
      this.status = status || { alerts: [] };
    });
    this.communication.listTemplates().subscribe((templates) => {
      this.templates = this.rankRows(templates || []);
      const current = this.templates.find(
        (row) => row._id === this.selectedTemplate?._id
      );
      const rx = this.templates.find((row) => row._id === 'rx-expiration');
      if (current) {
        this.selectedTemplate = current;
        this.refreshPreview();
      } else if (rx) {
        this.selectTemplate(rx);
      } else if (this.templates.length) {
        this.selectTemplate(this.templates[0]);
      }
    });
    this.communication.listRules().subscribe((rules) => {
      this.rules = this.rankRows(rules || []);
      const current = this.rules.find(
        (row) => row._id === this.selectedRule?._id
      );
      const rx = this.rules.find((row) => row._id === 'rx-expiration');
      if (current) {
        this.selectRule(current);
      } else if (rx) {
        this.selectRule(rx);
      } else if (this.rules.length) {
        this.selectRule(this.rules[0]);
      }
    });
    this.loadSends();
    this.communication.listExceptions().subscribe((rows) => {
      this.exceptions = rows || [];
    });
    this.communication.listAudit().subscribe((rows) => {
      this.audit = rows || [];
    });
  }

  loadSends(): void {
    this.communication.listSends(this.sendFilter).subscribe((rows) => {
      this.sends = rows || [];
    });
  }

  selectTemplate(template: CommunicationTemplate): void {
    this.selectedTemplate = { ...template };
    this.refreshPreview();
  }

  newTemplate(): void {
    this.selectedTemplate = this.blankTemplate();
    this.previewHtml = '';
  }

  refreshPreview(): void {
    const id = this.selectedTemplate?._id;
    if (!id || String(id).indexOf('custom-') === 0) {
      this.previewHtml = '';
      return;
    }
    this.communication.previewTemplate(id).subscribe({
      next: (res: any) => {
        this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(
          res?.html || res?.body || ''
        );
      },
      error: () => {
        this.previewHtml = '';
      },
    });
  }

  saveTemplate(): void {
    this.saving = true;
    this.communication.saveTemplate(this.selectedTemplate).subscribe({
      next: (template) => {
        this.saving = false;
        this.selectedTemplate = template;
        this.notification.show('success', 'Template saved');
        this.reload();
      },
      error: (err) => {
        this.saving = false;
        this.notification.show(
          'error',
          err?.error?.message || 'Could not save the template'
        );
      },
    });
  }

  testTemplate(): void {
    if (!this.selectedTemplate?._id) {
      return;
    }
    this.testing = true;
    this.communication
      .testTemplate(this.selectedTemplate._id, this.testEmail)
      .subscribe({
        next: () => {
          this.testing = false;
          this.notification.show('success', 'Test email sent');
          this.reload();
        },
        error: (err) => {
          this.testing = false;
          this.notification.show(
            'error',
            err?.error?.message || 'Could not send the test email'
          );
        },
      });
  }

  restoreVersion(version: number): void {
    this.communication
      .restoreTemplate(this.selectedTemplate._id, version)
      .subscribe({
        next: (template) => {
          this.selectedTemplate = template;
          this.notification.show('success', `Restored version ${version}`);
          this.reload();
        },
        error: (err) => {
          this.notification.show(
            'error',
            err?.error?.message || 'Could not restore that version'
          );
        },
      });
  }

  selectRule(rule: CommunicationRule): void {
    this.selectedRule = { ...rule };
    this.communication.estimateRule(rule._id).subscribe((count) => {
      this.estimate = count;
    });
  }

  saveRule(): void {
    if (!this.selectedRule) {
      return;
    }
    this.communication.saveRule(this.selectedRule).subscribe({
      next: (rule) => {
        this.selectedRule = rule;
        this.notification.show('success', 'Rule saved');
        this.reload();
      },
      error: (err) => {
        this.notification.show(
          'error',
          err?.error?.message || 'Could not save the rule'
        );
      },
    });
  }

  activateRule(): void {
    if (!this.selectedRule) {
      return;
    }
    this.dialog
      .open(OkCancelComponent, {
        width: '420px',
        data: {
          message:
            'Activate this rule? AutoOptiq will start sending to qualifying patients using the approved template.',
          confirmText: 'ACTIVATE',
        },
      })
      .afterClosed()
      .subscribe((ok) => {
        if (!ok) {
          return;
        }
        this.communication.activateRule(this.selectedRule._id).subscribe({
          next: (rule) => {
            this.selectedRule = rule;
            this.notification.show('success', 'Rule activated');
            this.reload();
          },
          error: (err) => {
            this.notification.show(
              'error',
              err?.error?.message || 'Send a test email before activating'
            );
          },
        });
      });
  }

  pauseRule(): void {
    if (!this.selectedRule) {
      return;
    }
    this.communication.pauseRule(this.selectedRule._id).subscribe({
      next: (rule) => {
        this.selectedRule = rule;
        this.notification.show('success', 'Rule paused');
        this.reload();
      },
      error: (err) => {
        this.notification.show(
          'error',
          err?.error?.message || 'Could not pause the rule'
        );
      },
    });
  }

  runNow(): void {
    this.running = true;
    this.communication.runNow().subscribe({
      next: (res: any) => {
        this.running = false;
        this.reload();
        this.notification.show(
          'success',
          `Processed ${res?.rx || 0} RX reminder(s) and ${res?.pickup || 0} pickup notice(s)`
        );
      },
      error: (err) => {
        this.running = false;
        this.notification.show(
          'error',
          err?.error?.message || 'Could not run due communications'
        );
      },
    });
  }

  patientName(row: any): string {
    const patient = row?.patient;
    if (!patient) {
      return '';
    }
    return [patient.lastName, patient.firstName].filter(Boolean).join(', ');
  }

  formatDate(value: any): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleString();
  }

  templateName(id: string): string {
    const match = this.templates.find((row) => row._id === id);
    return match?.name || id || '';
  }
}
